import { useState, useEffect, useCallback } from "react";
import { bookHref } from "@/utils/booking";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "@/utils/tracking";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Areas", href: "/locations" },
  { label: "Shop", href: "/shop" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Reviews", href: "#reviews" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "#contact" },
];

const NAV_BOOK_URL = bookHref({ campaign: "nav", content: "navbar-book-now" });
const MOBILE_BOOK_URL = bookHref({ campaign: "nav", content: "mobile-menu-book-now" });

/**
 * useHashNav
 * Navigates to `/#section` from any page: if already on home, smooth-scrolls to the
 * target; otherwise routes to home first, then scrolls once the section renders.
 */
function useHashNav() {
  const navigate = useNavigate();
  return useCallback((hash, onDone) => {
    navigate(`/${hash}`);
    if (typeof onDone === "function") onDone();
  }, [navigate]);
}

function MobileMenu({ onClose, navigateHash, isShop }) {
  return (
    <div data-testid="mobile-menu" className="xl:hidden nav-glass mobile-menu-enter px-6 pb-6 pt-2">
      {NAV_LINKS.map((link) => (
        link.href.startsWith('/') ? (
          <Link key={link.href} to={link.href} onClick={onClose}
            className="block py-3 text-slate-300 hover:text-white transition-colors border-b border-white/5">
            {link.label}
          </Link>
        ) : (
          <button
            key={link.href}
            type="button"
            onClick={() => navigateHash(link.href, onClose)}
            className="block w-full text-left py-3 text-slate-300 hover:text-white transition-colors border-b border-white/5"
          >
            {link.label}
          </button>
        )
      ))}
      <a href={MOBILE_BOOK_URL}
        onClick={() => { trackEvent("book_now_click", { location: "mobile-menu" }); onClose(); }}
        className="mt-4 block text-center bg-blue-600 text-white font-medium px-6 py-3 rounded-lg">
        {isShop ? "Book a consultation" : "Book Now"}
      </a>
    </div>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigateHash = useHashNav();

  const onScroll = useCallback(() => setScrolled(window.scrollY > 20), [setScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <nav
      data-testid="navbar"
      style={{ top: "var(--announcement-h, 0px)" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || pathname.startsWith("/shop") ? "nav-glass shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 py-1">
          <img
            src="/brand/logo-128.webp"
            srcSet="/brand/logo-128.webp 128w, /brand/logo-256.webp 256w"
            sizes="48px"
            fetchPriority="high"
            decoding="async"
            alt="I.T. Help of SWFL logo"
            width="48"
            height="48"
            className="h-11 md:h-12 w-auto object-contain"
          />
          <span className="text-lg md:text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="text-blue-500">I.T.</span> Help of SWFL
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            link.href.startsWith('/') ? (
              <Link key={link.href} to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200">
                {link.label}
              </Link>
            ) : (
              <button
                key={link.href}
                type="button"
                onClick={() => navigateHash(link.href)}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-slate-400 hover:text-white transition-colors duration-200 cursor-pointer"
              >
                {link.label}
              </button>
            )
          ))}
        </div>

        <div className="hidden xl:block">
          <a
            href={NAV_BOOK_URL}
            data-testid="nav-book-now-btn"
            onClick={() => trackEvent("book_now_click", { location: "navbar" })}
            className="bg-blue-600 text-white hover:bg-blue-700 font-medium px-6 py-2.5 rounded-lg transition-all text-sm btn-glow"
          >
            {pathname.startsWith("/shop") ? "Book a consultation" : "Book Now"}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="xl:hidden text-white inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg hover:bg-white/5 transition-colors"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && <MobileMenu onClose={() => setMobileOpen(false)} navigateHash={navigateHash} isShop={pathname.startsWith("/shop")} />}
    </nav>
  );
}
